import { supabase } from '../lib/supabase';

export interface AdminTenant {
  id: string;
  display_id?: number; // ID progressivo para exibição
  name: string;
  domain: string;
  plan: 'free' | 'premium';
  status: 'active' | 'inactive' | 'suspended' | 'cancelled';
  account_type: 'personal' | 'business' | 'enterprise';
  max_users?: number;
  created_at: string;
  updated_at?: string;
  // Campos calculados
  user_count?: number;
  total_messages?: number;
  last_activity?: string;
}

export interface AdminTenantUpdate {
  name?: string;
  domain?: string;
  plan?: 'free' | 'premium';
  status?: 'active' | 'inactive' | 'suspended' | 'cancelled';
  account_type?: 'personal' | 'business' | 'enterprise';
  max_users?: number;
}

export interface AdminAssistant {
  id: string;
  name: string;
  description?: string;
  ai_provider: string;
  model_id: string;
  tenant_id: string;
  tenant_name: string;
  tenant_plan: string;
  total_messages: number;
  total_conversations: number;
  total_cost: number;
  last_used?: string;
  created_at: string;
}

export interface AdminTenantStats {
  total_tenants: number;
  active_tenants: number;
  inactive_tenants: number;
  free_plan: number;
  premium_plan: number;
  total_users: number;
  total_messages: number;
}

export const adminService = {
  /**
   * Buscar todos os tenants com estatísticas
   */
  async getAllTenants(): Promise<AdminTenant[]> {
    try {
      // Buscar tenants
      const { data: tenants, error: tenantsError } = await supabase
        .from('tenants')
        .select('*')
        .order('created_at', { ascending: false });

      if (tenantsError) {
        console.error('Erro ao buscar tenants:', tenantsError);
        throw new Error('Erro ao buscar tenants');
      }

      // Para cada tenant, buscar contagem de usuários e mensagens
      const tenantsWithStats = await Promise.all(
        (tenants || []).map(async (tenant) => {
          // Contar usuários
          const { count: userCount } = await supabase
            .from('users')
            .select('*', { count: 'exact', head: true })
            .eq('tenant_id', tenant.id)
            .eq('status', 'active');

          // Contar mensagens (via conversas do tenant)
          const { data: conversations } = await supabase
            .from('conversations')
            .select('id')
            .eq('tenant_id', tenant.id);

          let totalMessages = 0;
          if (conversations && conversations.length > 0) {
            const conversationIds = conversations.map(c => c.id);
            const { count: messageCount } = await supabase
              .from('messages')
              .select('*', { count: 'exact', head: true })
              .in('conversation_id', conversationIds);
            
            totalMessages = messageCount || 0;
          }

          // Buscar última atividade (última mensagem ou último login)
          const { data: lastActivity } = await supabase
            .from('users')
            .select('last_activity')
            .eq('tenant_id', tenant.id)
            .order('last_activity', { ascending: false })
            .limit(1)
            .single();

          return {
            id: tenant.id,
            display_id: tenant.display_id,
            name: tenant.name,
            domain: tenant.domain,
            plan: tenant.plan,
            status: tenant.status,
            account_type: tenant.account_type || 'personal',
            max_users: tenant.max_users,
            created_at: tenant.created_at,
            updated_at: tenant.updated_at,
            user_count: userCount || 0,
            total_messages: totalMessages,
            last_activity: lastActivity?.last_activity || null
          };
        })
      );

      return tenantsWithStats;
    } catch (error) {
      console.error('Erro ao buscar tenants:', error);
      throw error;
    }
  },

  /**
   * Buscar um tenant específico por ID
   */
  async getTenantById(tenantId: string): Promise<AdminTenant | null> {
    try {
      const { data: tenant, error } = await supabase
        .from('tenants')
        .select('*')
        .eq('id', tenantId)
        .single();

      if (error) {
        console.error('Erro ao buscar tenant:', error);
        throw new Error('Erro ao buscar tenant');
      }

      if (!tenant) return null;

      // Buscar estatísticas
      const { count: userCount } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('tenant_id', tenant.id)
        .eq('status', 'active');

      return {
        id: tenant.id,
        display_id: tenant.display_id,
        name: tenant.name,
        domain: tenant.domain,
        plan: tenant.plan,
        status: tenant.status,
        account_type: tenant.account_type || 'personal',
        max_users: tenant.max_users,
        created_at: tenant.created_at,
        updated_at: tenant.updated_at,
        user_count: userCount || 0
      };
    } catch (error) {
      console.error('Erro ao buscar tenant:', error);
      throw error;
    }
  },

  /**
   * Atualizar um tenant
   */
  async updateTenant(tenantId: string, updates: AdminTenantUpdate): Promise<AdminTenant> {
    try {
      console.log('🔄 Atualizando tenant:', tenantId, updates);
      
      const { data: tenant, error } = await supabase
        .from('tenants')
        .update(updates)
        .eq('id', tenantId)
        .select()
        .single();

      if (error) {
        console.error('❌ Erro detalhado ao atualizar tenant:', error);
        console.error('Código:', error.code);
        console.error('Mensagem:', error.message);
        console.error('Detalhes:', error.details);
        throw new Error(`Erro ao atualizar tenant: ${error.message}`);
      }

      if (!tenant) {
        throw new Error('Tenant não encontrado após atualização');
      }

      console.log('✅ Tenant atualizado com sucesso:', tenant);

      return {
        id: tenant.id,
        display_id: tenant.display_id,
        name: tenant.name,
        domain: tenant.domain,
        plan: tenant.plan,
        status: tenant.status,
        account_type: tenant.account_type || 'personal',
        max_users: tenant.max_users,
        created_at: tenant.created_at,
        updated_at: tenant.updated_at
      };
    } catch (error) {
      console.error('❌ Erro ao atualizar tenant:', error);
      throw error;
    }
  },

  /**
   * Criar um novo tenant
   */
  async createTenant(tenantData: {
    name: string;
    domain: string;
    plan: 'free' | 'premium';
    account_type: 'personal' | 'business';
    max_users?: number;
  }): Promise<AdminTenant> {
    try {
      const { data: tenant, error } = await supabase
        .from('tenants')
        .insert({
          name: tenantData.name,
          domain: tenantData.domain,
          plan: tenantData.plan,
          account_type: tenantData.account_type,
          max_users: tenantData.max_users || (tenantData.plan === 'free' ? 1 : 100),
          status: 'active'
        })
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar tenant:', error);
        throw new Error('Erro ao criar tenant');
      }

      return {
        id: tenant.id,
        display_id: tenant.display_id,
        name: tenant.name,
        domain: tenant.domain,
        plan: tenant.plan,
        status: tenant.status,
        account_type: tenant.account_type,
        max_users: tenant.max_users,
        created_at: tenant.created_at,
        user_count: 0
      };
    } catch (error) {
      console.error('Erro ao criar tenant:', error);
      throw error;
    }
  },

  /**
   * Deletar um tenant (soft delete - muda status para cancelled)
   */
  async deleteTenant(tenantId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('tenants')
        .update({ status: 'cancelled', updated_at: new Date().toISOString() })
        .eq('id', tenantId);

      if (error) {
        console.error('Erro ao deletar tenant:', error);
        throw new Error('Erro ao deletar tenant');
      }
    } catch (error) {
      console.error('Erro ao deletar tenant:', error);
      throw error;
    }
  },

  /**
   * Buscar estatísticas dos últimos 30 dias
   */
  async getLast30DaysStats() {
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const thirtyDaysAgoISO = thirtyDaysAgo.toISOString();

      // Tenants ativos nos últimos 30 dias (que tiveram mensagens)
      const { data: activeTenantsData } = await supabase
        .from('conversations')
        .select('tenant_id')
        .gte('updated_at', thirtyDaysAgoISO);

      const uniqueTenants = new Set(activeTenantsData?.map(c => c.tenant_id) || []);
      const activeTenantsCount = uniqueTenants.size;

      // Usuários ativos nos últimos 30 dias (que enviaram mensagens)
      const { data: messagesData } = await supabase
        .from('messages')
        .select('conversation_id')
        .gte('created_at', thirtyDaysAgoISO);

      // Buscar conversations para pegar user_id
      const conversationIds = [...new Set(messagesData?.map(m => m.conversation_id) || [])];
      
      let activeUsersCount = 0;
      if (conversationIds.length > 0) {
        const { data: conversationsData } = await supabase
          .from('conversations')
          .select('user_id')
          .in('id', conversationIds);

        const uniqueUsers = new Set(conversationsData?.map(c => c.user_id) || []);
        activeUsersCount = uniqueUsers.size;
      }

      // Total de mensagens dos últimos 30 dias
      const { count: totalMessages } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', thirtyDaysAgoISO);

      return {
        active_tenants_30d: activeTenantsCount,
        active_users_30d: activeUsersCount,
        total_messages_30d: totalMessages || 0
      };
    } catch (error) {
      console.error('Erro ao buscar estatísticas dos últimos 30 dias:', error);
      throw error;
    }
  },

  /**
   * Buscar métricas por IA com custos
   */
  async getAIMetrics(days: number = 30) {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      const startDateISO = startDate.toISOString();

      // Buscar mensagens de conversas normais
      const { data: allMessages } = await supabase
        .from('messages')
        .select('role, content, conversation_id, created_at, prompt_tokens, completion_tokens, total_tokens, cost, model_id')
        .gte('created_at', startDateISO);

      // Buscar mensagens de assistentes
      const { data: assistantMessages } = await supabase
        .from('assistant_messages')
        .select('role, content, conversation_id, timestamp, prompt_tokens, completion_tokens, total_tokens, cost, assistant_id')
        .gte('timestamp', startDateISO);

      // Buscar conversations para saber qual AI foi usada
      const conversationIds = [...new Set(allMessages?.map(m => m.conversation_id) || [])];
      const { data: conversations } = await supabase
        .from('conversations')
        .select('id, ai_id, model_id, user_id')
        .in('id', conversationIds);

      // Buscar assistants para saber qual AI foi usada
      const assistantIds = [...new Set(assistantMessages?.map(m => m.assistant_id) || [])];
      const { data: assistants } = await supabase
        .from('assistants')
        .select('id, ai_provider, model_id')
        .in('id', assistantIds);

      // Criar mapa de conversation_id -> {ai_id, model_id, user_id}
      const conversationMap = new Map();
      conversations?.forEach(conv => {
        conversationMap.set(conv.id, {
          ai_id: conv.ai_id,
          model_id: conv.model_id,
          user_id: conv.user_id
        });
      });

      // Criar mapa de assistant_id -> {ai_provider, model_id}
      const assistantMap = new Map();
      assistants?.forEach(asst => {
        assistantMap.set(asst.id, {
          ai_id: asst.ai_provider,
          model_id: asst.model_id
        });
      });

      // Custos estimados por 1M tokens (em USD)
      const aiCosts = {
        openai: {
          input: 5.0,  // GPT-4o input
          output: 15.0 // GPT-4o output
        },
        anthropic: {
          input: 3.0,  // Claude 3.5 Sonnet input
          output: 15.0 // Claude 3.5 Sonnet output
        },
        google: {
          input: 1.25,  // Gemini 1.5 Pro input
          output: 5.0   // Gemini 1.5 Pro output
        }
      };

      // Processar métricas por IA
      const aiMetrics: any = {
        openai: { name: 'GPT (OpenAI)', messages: 0, users: new Set(), inputTokens: 0, outputTokens: 0, cost: 0 },
        anthropic: { name: 'Claude (Anthropic)', messages: 0, users: new Set(), inputTokens: 0, outputTokens: 0, cost: 0 },
        google: { name: 'Gemini (Google)', messages: 0, users: new Set(), inputTokens: 0, outputTokens: 0, cost: 0 }
      };

      allMessages?.forEach(msg => {
        const conv = conversationMap.get(msg.conversation_id);
        if (!conv || !conv.ai_id) return;

        const provider = conv.ai_id.toLowerCase();
        if (!aiMetrics[provider]) return;

        aiMetrics[provider].messages++;
        if (conv.user_id) {
          aiMetrics[provider].users.add(conv.user_id);
        }

        // ✅ Usar custo salvo se disponível (mais preciso!)
        if (msg.cost && msg.cost > 0) {
          aiMetrics[provider].cost += msg.cost;
          // Usar tokens reais se disponíveis
          if (msg.prompt_tokens && msg.completion_tokens) {
            aiMetrics[provider].inputTokens += msg.prompt_tokens;
            aiMetrics[provider].outputTokens += msg.completion_tokens;
          }
        } else {
          // ❌ Fallback: Calcular manualmente
          let promptTokens = 0;
          let completionTokens = 0;

          if (msg.prompt_tokens && msg.completion_tokens) {
            // Tokens reais da API
            promptTokens = msg.prompt_tokens;
            completionTokens = msg.completion_tokens;
          } else {
            // Estimar tokens (aproximadamente 4 caracteres por token)
            const tokenEstimate = Math.ceil((msg.content?.length || 0) / 4);
            if (msg.role === 'user') {
              promptTokens = tokenEstimate;
            } else if (msg.role === 'assistant') {
              completionTokens = tokenEstimate;
            }
          }

          // Calcular custos manualmente (fallback para mensagens antigas)
          aiMetrics[provider].inputTokens += promptTokens;
          aiMetrics[provider].outputTokens += completionTokens;
          aiMetrics[provider].cost += (promptTokens / 1000000) * aiCosts[provider].input;
          aiMetrics[provider].cost += (completionTokens / 1000000) * aiCosts[provider].output;
        }
      });

      // Processar mensagens de assistentes
      assistantMessages?.forEach(msg => {
        const asst = assistantMap.get(msg.assistant_id);
        if (!asst || !asst.ai_id) return;

        const provider = asst.ai_id.toLowerCase();
        if (!aiMetrics[provider]) return;

        aiMetrics[provider].messages++;
        // Assistentes não têm user_id direto, então não contamos usuários únicos aqui

        // Usar custo salvo se disponível
        if (msg.cost && msg.cost > 0) {
          aiMetrics[provider].cost += msg.cost;
          if (msg.prompt_tokens && msg.completion_tokens) {
            aiMetrics[provider].inputTokens += msg.prompt_tokens;
            aiMetrics[provider].outputTokens += msg.completion_tokens;
          }
        } else {
          // Fallback: Calcular manualmente
          let promptTokens = 0;
          let completionTokens = 0;

          if (msg.prompt_tokens && msg.completion_tokens) {
            promptTokens = msg.prompt_tokens;
            completionTokens = msg.completion_tokens;
          } else {
            const tokenEstimate = Math.ceil((msg.content?.length || 0) / 4);
            if (msg.role === 'user') {
              promptTokens = tokenEstimate;
            } else if (msg.role === 'assistant') {
              completionTokens = tokenEstimate;
            }
          }

          aiMetrics[provider].inputTokens += promptTokens;
          aiMetrics[provider].outputTokens += completionTokens;
          aiMetrics[provider].cost += (promptTokens / 1000000) * aiCosts[provider].input;
          aiMetrics[provider].cost += (completionTokens / 1000000) * aiCosts[provider].output;
        }
      });

      // Converter Set para count e formatar resultado
      return Object.entries(aiMetrics).map(([key, data]: [string, any]) => ({
        providerId: key,
        name: data.name,
        messages: data.messages,
        activeUsers: data.users.size,
        inputTokens: data.inputTokens,
        outputTokens: data.outputTokens,
        totalTokens: data.inputTokens + data.outputTokens,
        cost: data.cost,
        enabled: data.messages > 0
      }));
    } catch (error) {
      console.error('Erro ao buscar métricas de IA:', error);
      throw error;
    }
  },

  /**
   * Buscar estatísticas dia a dia dos últimos 30 dias para gráficos
   */
  async getDailyStats() {
    try {
      const days = [];
      const today = new Date();
      
      // Criar array com os últimos 30 dias
      for (let i = 29; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        date.setHours(0, 0, 0, 0);
        days.push(date);
      }

      // Buscar todas as mensagens dos últimos 30 dias
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { data: allMessages } = await supabase
        .from('messages')
        .select('created_at, conversation_id')
        .gte('created_at', thirtyDaysAgo.toISOString());

      // Buscar todas as conversations para mapear tenant_id e user_id
      const conversationIds = [...new Set(allMessages?.map(m => m.conversation_id) || [])];
      const { data: allConversations } = await supabase
        .from('conversations')
        .select('id, tenant_id, user_id, updated_at')
        .in('id', conversationIds);

      // Criar mapa de conversation_id -> {tenant_id, user_id}
      const conversationMap = new Map();
      allConversations?.forEach(conv => {
        conversationMap.set(conv.id, {
          tenant_id: conv.tenant_id,
          user_id: conv.user_id
        });
      });

      // Processar dados dia a dia
      const dailyData = days.map(day => {
        const nextDay = new Date(day);
        nextDay.setDate(nextDay.getDate() + 1);

        // Mensagens do dia
        const messagesOfDay = allMessages?.filter(msg => {
          const msgDate = new Date(msg.created_at);
          return msgDate >= day && msgDate < nextDay;
        }) || [];

        // Tenants ativos do dia (únicos)
        const tenantsOfDay = new Set(
          messagesOfDay
            .map(msg => conversationMap.get(msg.conversation_id)?.tenant_id)
            .filter(Boolean)
        );

        // Usuários ativos do dia (únicos)
        const usersOfDay = new Set(
          messagesOfDay
            .map(msg => conversationMap.get(msg.conversation_id)?.user_id)
            .filter(Boolean)
        );

        return {
          date: day.toISOString().split('T')[0], // YYYY-MM-DD
          dateFormatted: `${day.getDate()}/${day.getMonth() + 1}`, // DD/MM
          tenants: tenantsOfDay.size,
          users: usersOfDay.size,
          messages: messagesOfDay.length
        };
      });

      return dailyData;
    } catch (error) {
      console.error('Erro ao buscar estatísticas diárias:', error);
      throw error;
    }
  },

  /**
   * Buscar estatísticas gerais do sistema
   */
  async getSystemStats(): Promise<AdminTenantStats> {
    try {
      // Contar tenants
      const { count: totalTenants } = await supabase
        .from('tenants')
        .select('*', { count: 'exact', head: true });

      const { count: activeTenants } = await supabase
        .from('tenants')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active');

      const { count: inactiveTenants } = await supabase
        .from('tenants')
        .select('*', { count: 'exact', head: true })
        .in('status', ['inactive', 'suspended', 'cancelled']);

      const { count: freePlan } = await supabase
        .from('tenants')
        .select('*', { count: 'exact', head: true })
        .eq('plan', 'free');

      const { count: premiumPlan } = await supabase
        .from('tenants')
        .select('*', { count: 'exact', head: true })
        .eq('plan', 'premium');

      // Contar usuários totais
      const { count: totalUsers } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active');

      // Contar mensagens totais
      const { count: totalMessages } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true });

      return {
        total_tenants: totalTenants || 0,
        active_tenants: activeTenants || 0,
        inactive_tenants: inactiveTenants || 0,
        free_plan: freePlan || 0,
        premium_plan: premiumPlan || 0,
        total_users: totalUsers || 0,
        total_messages: totalMessages || 0
      };
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      throw error;
    }
  },

  /**
   * Buscar usuários de um tenant específico
   */
  async getTenantUsers(tenantId: string) {
    try {
      const { data: users, error } = await supabase
        .from('users')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao buscar usuários do tenant:', error);
        throw new Error('Erro ao buscar usuários');
      }

      return users || [];
    } catch (error) {
      console.error('Erro ao buscar usuários do tenant:', error);
      throw error;
    }
  },

  /**
   * Buscar todos os usuários do sistema
   */
  async getAllUsers() {
    try {
      const { data: users, error } = await supabase
        .from('users')
        .select(`
          *,
          tenant:tenants!users_tenant_id_fkey(id, name, domain, plan, status, display_id)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao buscar usuários:', error);
        throw new Error('Erro ao buscar usuários');
      }

      return users || [];
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      throw error;
    }
  },

  /**
   * Atualizar um usuário
   */
  async updateUser(userId: string, updates: {
    name?: string;
    email?: string;
    role?: 'owner' | 'manager' | 'user';
    status?: 'active' | 'inactive' | 'suspended';
  }) {
    try {
      const { data: user, error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        console.error('Erro ao atualizar usuário:', error);
        throw new Error('Erro ao atualizar usuário');
      }

      return user;
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      throw error;
    }
  },

  /**
   * Buscar todos os assistentes com métricas
   */
  async getAssistants(page: number = 1, limit: number = 20): Promise<{
    assistants: AdminAssistant[];
    total: number;
    totalPages: number;
  }> {
    try {
      const offset = (page - 1) * limit;

      // Buscar total de assistentes
      const { count } = await supabase
        .from('assistants')
        .select('*', { count: 'exact', head: true });

      // Buscar assistentes com dados do tenant
      const { data: assistants, error } = await supabase
        .from('assistants')
        .select(`
          id,
          name,
          description,
          ai_provider,
          model_id,
          tenant_id,
          created_at,
          tenants!inner (
            name,
            plan
          )
        `)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        console.error('Erro ao buscar assistentes:', error);
        throw error;
      }

      // Para cada assistente, buscar métricas
      const assistantsWithMetrics = await Promise.all(
        (assistants || []).map(async (asst: any) => {
          // Buscar total de mensagens
          const { count: messagesCount } = await supabase
            .from('assistant_messages')
            .select('*', { count: 'exact', head: true })
            .eq('assistant_id', asst.id);

          // Buscar total de conversas
          const { count: conversationsCount } = await supabase
            .from('assistant_conversations')
            .select('*', { count: 'exact', head: true })
            .eq('assistant_id', asst.id);

          // Buscar custo total
          const { data: costData } = await supabase
            .from('assistant_messages')
            .select('cost')
            .eq('assistant_id', asst.id)
            .eq('role', 'assistant');

          const totalCost = costData?.reduce((sum, msg) => sum + (msg.cost || 0), 0) || 0;

          // Buscar última utilização
          const { data: lastMessage } = await supabase
            .from('assistant_messages')
            .select('timestamp')
            .eq('assistant_id', asst.id)
            .order('timestamp', { ascending: false })
            .limit(1)
            .single();

          return {
            id: asst.id,
            name: asst.name,
            description: asst.description,
            ai_provider: asst.ai_provider,
            model_id: asst.model_id,
            tenant_id: asst.tenant_id,
            tenant_name: asst.tenants?.name || 'N/A',
            tenant_plan: asst.tenants?.plan || 'N/A',
            total_messages: messagesCount || 0,
            total_conversations: conversationsCount || 0,
            total_cost: totalCost,
            last_used: lastMessage?.timestamp,
            created_at: asst.created_at
          };
        })
      );

      // Ordenar por total de mensagens (maior uso)
      assistantsWithMetrics.sort((a, b) => b.total_messages - a.total_messages);

      return {
        assistants: assistantsWithMetrics,
        total: count || 0,
        totalPages: Math.ceil((count || 0) / limit)
      };
    } catch (error) {
      console.error('Erro ao buscar assistentes:', error);
      throw error;
    }
  }
};

